import React, { useState, useEffect } from 'react';
import { Prompt, PromptFormData } from '../types/admin';

interface PromptFormProps {
  prompt?: Prompt;
  onSubmit: (data: PromptFormData) => void;
  onCancel: () => void;
  loading: boolean;
}

const PromptForm: React.FC<PromptFormProps> = ({
  prompt,
  onSubmit,
  onCancel,
  loading
}) => {
  const [formData, setFormData] = useState<PromptFormData>({
    name: '',
    prompt_text: '',
    version: 1,
    temperature: 0.3,
    max_tokens: 1000
  });

  useEffect(() => {
    if (prompt) {
      setFormData({
        name: prompt.name,
        prompt_text: prompt.prompt_text,
        version: prompt.version || 1,
        temperature: prompt.temperature,
        max_tokens: prompt.max_tokens
      });
    }
  }, [prompt]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isSystemPrompt = prompt?.id === 1;

  return (
    <div className="prompt-form">
      <div className="form-header">
        <h3>{prompt ? `Editar Prompt #${prompt.id}` : 'Crear Nuevo Prompt'}</h3>
        {isSystemPrompt && (
          <div className="system-warning">
            ⚠️ Estás editando el prompt del sistema. Los cambios afectarán todas las conversaciones.
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Nombre del Prompt</label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            required
            disabled={isSystemPrompt || loading}
            placeholder="Ej: Cliente Premium, Seguimiento Post-Venta..."
          />
        </div>

        <div className="form-group">
          <label htmlFor="prompt_text">Contenido del Prompt</label>
          <div className="textarea-container">
            <textarea
              id="prompt_text"
              value={formData.prompt_text}
              onChange={(e) => setFormData(prev => ({ ...prev, prompt_text: e.target.value }))}
              required
              disabled={loading}
              rows={15}
              placeholder="Escribe el prompt aquí. Puedes usar variables como {fecha}, {telefono}, {nombre}, {datos_cliente}, {estrategia}"
            />
            <div className="char-count">
              {formData.prompt_text.length} caracteres
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="version">Versión</label>
            <input
              type="number"
              id="version"
              value={formData.version}
              onChange={(e) => setFormData(prev => ({ ...prev, version: parseInt(e.target.value) || 1 }))}
              min="1"
              step="1"
              disabled={loading}
            />
            <small>Número de versión del prompt</small>
          </div>

          <div className="form-group">
            <label htmlFor="temperature">Temperature</label>
            <input
              type="number"
              id="temperature"
              value={formData.temperature}
              onChange={(e) => setFormData(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
              min="0"
              max="1"
              step="0.1"
              disabled={loading}
            />
            <small>Controla la creatividad (0 = determinístico, 1 = creativo)</small>
          </div>

          <div className="form-group">
            <label htmlFor="max_tokens">Máximo Tokens</label>
            <input
              type="number"
              id="max_tokens"
              value={formData.max_tokens}
              onChange={(e) => setFormData(prev => ({ ...prev, max_tokens: parseInt(e.target.value) }))}
              min="100"
              max="4000"
              step="100"
              disabled={loading}
            />
            <small>Límite de respuesta de la IA</small>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !formData.name.trim() || !formData.prompt_text.trim()}
          >
            {loading ? 'Guardando...' : (prompt ? 'Actualizar' : 'Crear')}
          </button>
        </div>
      </form>

      <style>{`
        .prompt-form {
          max-width: 800px;
          margin: 0 auto;
        }

        .form-header h3 {
          margin: 0 0 20px 0;
          color: #333;
          font-size: 20px;
        }

        .system-warning {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          padding: 12px;
          border-radius: 4px;
          color: #856404;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #333;
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          font-family: inherit;
        }

        .form-group textarea {
          resize: vertical;
          font-family: monospace;
          line-height: 1.4;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #4CAF50;
          box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
        }

        .form-group small {
          display: block;
          margin-top: 4px;
          color: #666;
          font-size: 12px;
        }

        .textarea-container {
          position: relative;
        }

        .char-count {
          position: absolute;
          bottom: 8px;
          right: 8px;
          background: rgba(255, 255, 255, 0.9);
          padding: 2px 6px;
          border-radius: 2px;
          font-size: 11px;
          color: #666;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 20px;
        }

        .form-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #e0e0e0;
        }

        .btn-primary,
        .btn-secondary {
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 500;
        }

        .btn-primary {
          background: #4CAF50;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #45a049;
        }

        .btn-primary:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: #f5f5f5;
          color: #333;
          border: 1px solid #ddd;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #e8e8e8;
        }

        .btn-secondary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default PromptForm;
